import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { formatDistanceToNow } from 'date-fns';
import { useAccount } from '../../context/AccountContext';
import {
  GET_TINYCMS_DELETED_PAGES,
  GET_TINYCMS_PAGE,
  GET_TINYCMS_PAGES,
  GET_TINYCMS_SITE,
  RUN_TINYCMS_PAGE_COPY_EDIT,
} from '../../graphql';
import {
  CREATE_TINYCMS_PREVIEW,
  DELETE_TINYCMS_PAGE,
  RESTORE_TINYCMS_PAGE,
} from '../../graphql/mutations';
import { RecentlyDeleted, type DeletedItem } from './RecentlyDeleted';
import { CmsNavigation } from './CmsNavigation';
import {
  ApprovalHistory,
  articleStatus,
  Finding,
  FindingsList,
  ItemState,
  PreviewButton,
  PublishPanel,
} from './shared';

/**
 * Content → Pages ("TinyCMS UX optimization review v5.zip", Prototype v3):
 * the site's pages and the parts on every page (header, footer) in a rail;
 * the one picked as a read-only preview of its working copy in the site's own
 * theme, desktop or mobile, with its blocks outlined on request; and, when the
 * agent has changed it, what's waiting and the way to publish it.
 *
 * Pages and the header & footer moved here from Site. The agent writes them
 * (MCP); a member reads, checks and publishes. Landing pages and forms are in
 * the rendering but not built yet.
 */

interface PageRow {
  id: string;
  path: string;
  title: string;
  workingHash: string;
  state: ItemState & { live: (ItemState['live'] & { path?: string }) | null };
  blockCount?: number;
  updatedAt?: string;
}

type Device = 'desktop' | 'mobile';
type Part = 'header' | 'footer';

/** A page's status in the rail and its heading: colour, words, and the short tag. */
function pageStatus(state: PageRow['state']) {
  const s = articleStatus(state);
  if (s.tone === 'review')
    return {
      dot: 'var(--ink-amber)',
      color: 'var(--ink-amber)',
      label: 'Changes pending',
      tag: 'Pending',
    };
  if (s.tone === 'draft')
    return {
      dot: 'var(--ink-line3)',
      color: 'var(--ink-ink3)',
      label: 'Not published',
      tag: 'Draft',
    };
  if (s.tone === 'scheduled')
    return {
      dot: 'var(--ink-accent)',
      color: 'var(--ink-accent)',
      label: 'Scheduled',
      tag: 'Scheduled',
    };
  return { dot: 'var(--ink-green)', color: 'var(--ink-green-ink)', label: 'Live', tag: '' };
}

const ago = (iso?: string | null) =>
  iso ? `updated ${formatDistanceToNow(new Date(iso), { addSuffix: true })}` : '';

interface NavLink {
  label: string;
  href?: string | null;
  children?: NavLink[];
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Links counted the way a reader sees them: dropdown entries included. */
const countLinks = (links?: NavLink[] | null) =>
  (links || []).reduce((n, l) => n + (l.children?.length ? l.children.length : 1), 0);

export function CmsPagesView({ initial }: { initial?: string | null }) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: siteData } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: deletedData, refetch: refetchDeleted } = useQuery(GET_TINYCMS_DELETED_PAGES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const deleted: DeletedItem[] = deletedData?.tinycmsDeletedPages || [];
  const pages: PageRow[] = useMemo(() => data?.tinycmsPages || [], [data]);
  const site = siteData?.tinycmsSite;
  const homeSlug: string | null = site?.working?.layout?.homePage || null;
  const nav = site?.working?.navigation || {};

  // Home first, then the rest in the server's order (groups follow their parent).
  const ordered = useMemo(() => {
    const home = pages.find((p) => homeSlug && p.path === `/${homeSlug}`);
    return home ? [home, ...pages.filter((p) => p !== home)] : pages;
  }, [pages, homeSlug]);

  // Nothing picked yet means the top of the list, whatever it is when the site
  // settings arrive (the home page moves to the top then), so no default is
  // stored that could go stale.
  const [sel, setSel] = useState<string | null>(initial || null);
  const onRestored = (id: string) => {
    void refetch();
    void refetchDeleted();
    setSel(id);
  };

  if (loading && !pages.length)
    return (
      <p className="ink-muted" style={{ marginTop: 28 }}>
        Loading…
      </p>
    );
  if (!pages.length) {
    return (
      <div className="pg-empty">
        <div className="pg-empty-title">No pages yet</div>
        <p>
          Ask your agent for one: “Create a pricing page”, or “Make a home page with a hero, three
          features and our latest articles”. It appears here for you to preview and publish.
        </p>
        {accountId ? (
          <div style={{ marginTop: 20, textAlign: 'left' }}>
            <RecentlyDeleted
              accountId={accountId}
              items={deleted}
              restoreMutation={RESTORE_TINYCMS_PAGE}
              onRestored={onRestored}
              note="Restored pages come back as drafts. Publish one again to put it back on your site."
            />
          </div>
        ) : null}
      </div>
    );
  }

  const part: Part | null = sel === 'header' || sel === 'footer' ? sel : null;
  const page = part ? null : ordered.find((p) => p.id === sel) || ordered[0];
  const homeId = ordered.find((p) => homeSlug && p.path === `/${homeSlug}`)?.id;

  return (
    <div className="pg">
      <aside className="pg-rail">
        <div>
          <div className="pg-rail-head">SITE PAGES</div>
          {ordered.map((p) => {
            const st = pageStatus(p.state);
            const nested = p.path.split('/').length > 2;
            const isHome = p.id === homeId;
            return (
              <button
                key={p.id}
                type="button"
                className="pg-rail-item"
                data-active={page?.id === p.id ? 'true' : undefined}
                style={nested ? { paddingLeft: 24 } : undefined}
                onClick={() => setSel(p.id)}
              >
                <span className="pg-dot" style={{ background: st.dot }} />
                <span className="pg-rail-text">
                  <span className="pg-rail-title">{p.title}</span>
                  <span className="pg-rail-path">{isHome ? '/' : p.path}</span>
                </span>
                {st.tag ? (
                  <span className="pg-rail-tag" style={{ color: st.color }}>
                    {st.tag}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        <div>
          <div className="pg-rail-head">ON EVERY PAGE</div>
          {(
            [
              [
                'header',
                'Header',
                `Site name · ${plural(countLinks(nav.header) + (nav.cta ? 1 : 0), 'link')}`,
              ],
              [
                'footer',
                'Footer',
                `${nav.footerText ? 'Copyright · ' : ''}${plural(
                  countLinks(nav.footer) +
                    (nav.footerColumns || []).reduce(
                      (n: number, c: { links?: NavLink[] }) => n + (c.links?.length || 0),
                      0
                    ),
                  'link'
                )}`,
              ],
            ] as Array<[Part, string, string]>
          ).map(([id, title, sub]) => (
            <button
              key={id}
              type="button"
              className="pg-rail-item"
              data-active={sel === id ? 'true' : undefined}
              onClick={() => setSel(id)}
            >
              <span
                className="pg-dot"
                style={{
                  background: site?.state?.hasUnpublishedChanges
                    ? 'var(--ink-amber)'
                    : 'var(--ink-green)',
                }}
              />
              <span className="pg-rail-text">
                <span className="pg-rail-title">{title}</span>
                <span className="pg-rail-sub">{sub}</span>
              </span>
            </button>
          ))}
        </div>
        {accountId ? (
          <RecentlyDeleted
            accountId={accountId}
            items={deleted}
            restoreMutation={RESTORE_TINYCMS_PAGE}
            onRestored={onRestored}
            note="Restored pages come back as drafts. Publish one again to put it back on your site."
          />
        ) : null}
      </aside>

      <div className="pg-main">
        {part ? (
          <PartPane part={part} site={site} />
        ) : page ? (
          <PagePane
            key={page.id}
            row={page}
            isHome={page.id === homeId}
            siteUrl={site?.siteUrl || null}
            onChanged={() => void refetch()}
            onDeleted={() => {
              setSel(null);
              void refetch();
              void refetchDeleted();
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function DeviceSwitch({ device, onDevice }: { device: Device; onDevice: (d: Device) => void }) {
  return (
    <div className="pg-seg" role="radiogroup" aria-label="Preview size">
      {(['desktop', 'mobile'] as Device[]).map((d) => (
        <button
          key={d}
          type="button"
          role="radio"
          aria-checked={device === d}
          data-active={device === d ? 'true' : undefined}
          onClick={() => onDevice(d)}
        >
          {d === 'desktop' ? 'Desktop' : 'Mobile'}
        </button>
      ))}
    </div>
  );
}

/**
 * The working copy of a page (or the whole site) in a frame, at desktop or
 * phone width. The preview link is minted once; the frame loads its page
 * with ?preview= so no cookie is needed (see smagent-sites middleware).
 */
export function WorkingPreview({
  pageId,
  articleId,
  path,
  outline,
  device,
  height = 760,
}: {
  pageId?: string;
  articleId?: string;
  /** Path to show instead of the one the link names (the home page lives at /). */
  path?: string;
  outline?: 'blocks' | Part | null;
  device: Device;
  height?: number | string;
}) {
  const { accountId } = useAccount();
  const [create] = useMutation(CREATE_TINYCMS_PREVIEW);
  const [link, setLink] = useState<{
    origin: string;
    next: string;
    token: string;
    /** Anything else the link carries (?account= on a local site). */
    extra: [string, string][];
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!accountId) return;
    let alive = true;
    setLink(null);
    setError('');
    create({ variables: { accountId, pageId: pageId || null, articleId: articleId || null } })
      .then(({ data }) => {
        const url = data?.createTinycmsPreview?.url;
        if (!alive || !url) return;
        const u = new URL(url);
        const token = u.pathname.split('/preview/')[1] || '';
        const extra = [...u.searchParams.entries()].filter(([k]) => k !== 'next');
        setLink({ origin: u.origin, next: u.searchParams.get('next') || '/', token, extra });
      })
      .catch((err: Error) => alive && setError(err.message));
    return () => {
      alive = false;
    };
  }, [accountId, pageId, articleId, create]);

  // Desktop is drawn at a real desktop width and scaled down to fit, so it
  // never falls back to the site's phone layout in a narrow window.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [wrapWidth, setWrapWidth] = useState(0);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWrapWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale =
    device === 'desktop' && wrapWidth && wrapWidth < DESKTOP_WIDTH ? wrapWidth / DESKTOP_WIDTH : 1;
  // Labels in "Show blocks" stay readable when the desktop view is scaled down.
  const labelScale = scale < 1 ? Math.min(4, Math.round((1 / scale) * 2) / 2) : 1;

  const src = useMemo(() => {
    if (!link) return null;
    const u = new URL(path || link.next, link.origin);
    for (const [k, v] of link.extra) u.searchParams.set(k, v);
    u.searchParams.set('preview', link.token);
    u.searchParams.set('embed', '1');
    if (outline) u.searchParams.set('outline', outline);
    if (outline && labelScale > 1) u.searchParams.set('ui', String(labelScale));
    return u.toString();
  }, [link, path, outline, labelScale]);

  const frameStyle: React.CSSProperties =
    scale < 1
      ? {
          width: DESKTOP_WIDTH,
          height: typeof height === 'number' ? height / scale : `calc((${height}) / ${scale})`,
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
        }
      : { height };

  return (
    <div className="pg-stage">
      <div
        ref={wrapRef}
        className="pg-frame-wrap"
        data-device={device}
        style={scale < 1 ? { height } : undefined}
      >
        {error ? (
          <p className="pg-frame-note">{error}</p>
        ) : src ? (
          <iframe
            title="Preview of the working copy"
            src={src}
            className="pg-frame"
            style={frameStyle}
          />
        ) : (
          <p className="pg-frame-note">Loading the preview…</p>
        )}
      </div>
    </div>
  );
}

/** The width the desktop preview is laid out at. */
const DESKTOP_WIDTH = 1280;

function PagePane({
  row,
  isHome,
  siteUrl,
  onChanged,
  onDeleted,
}: {
  row: PageRow;
  isHome: boolean;
  siteUrl: string | null;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const { accountId } = useAccount();
  const [deletePage, { loading: deleting }] = useMutation(DELETE_TINYCMS_PAGE);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [showBlocks, setShowBlocks] = useState(false);
  const [checksOpen, setChecksOpen] = useState(false);
  const st = pageStatus(row.state);
  const live = Boolean(row.state.live);
  const path = isHome ? '/' : row.path;
  const waiting = !live || row.state.hasUnpublishedChanges;

  const handleDelete = async () => {
    setDeleteError(null);
    const scheduled = Boolean(row.state.scheduled);
    const question =
      live || scheduled
        ? `Delete “${row.title}”?\n\nIt’s ${live ? 'live' : 'scheduled'} at ${row.path}. ` +
          'Deleting takes it off your site now. You can restore it from Recently deleted for 30 days.'
        : `Delete “${row.title}”?\n\nIt isn’t on your site. You can restore it from Recently deleted for 30 days.`;
    if (!window.confirm(question)) return;
    try {
      await deletePage({ variables: { accountId, id: row.id } });
      onDeleted();
    } catch (err) {
      // e.g. the header links to it, or it's the home page: the server says what to change.
      setDeleteError(err instanceof Error ? err.message : 'Couldn’t delete the page');
    }
  };

  const openChecks = () => {
    setChecksOpen(true);
    setTimeout(
      () => document.getElementById('pg-checks')?.scrollIntoView({ behavior: 'smooth' }),
      0
    );
  };

  return (
    <>
      <div className="pg-head">
        <div style={{ minWidth: 0 }}>
          <div className="pg-title">{row.title}</div>
          <div className="pg-meta">
            <span className="pg-status" style={{ color: st.color }}>
              <span className="pg-dot" style={{ background: st.dot }} />
              {st.label}
            </span>
            <span>·</span>
            {live && siteUrl ? (
              <a
                href={`${siteUrl}${path === '/' ? '' : path}`}
                target="_blank"
                rel="noreferrer"
                className="pg-url"
              >
                {siteUrl.replace(/^https?:\/\//, '')}
                {path === '/' ? '' : path} ↗
              </a>
            ) : (
              <span className="pg-url" data-muted="true">
                {path}
              </span>
            )}
            {typeof row.blockCount === 'number' ? (
              <>
                <span>·</span>
                <span>
                  {row.blockCount} block{row.blockCount === 1 ? '' : 's'}
                  {row.updatedAt ? ` · ${ago(row.updatedAt)}` : ''}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="pg-tools">
          <button
            type="button"
            className="pg-check"
            aria-pressed={showBlocks}
            onClick={() => setShowBlocks((v) => !v)}
          >
            <span className="pg-box" data-on={showBlocks ? 'true' : undefined} />
            Show blocks
          </button>
          <DeviceSwitch device={device} onDevice={setDevice} />
          <PreviewButton pageId={row.id} label="Preview" />
        </div>
      </div>

      {waiting ? (
        <div className="pg-action">
          <span
            className="pg-dot"
            style={{ background: 'var(--ink-amber)', width: 8, height: 8 }}
          />
          <div className="pg-action-text">
            <div className="pg-action-title">
              {live ? 'Your agent changed this page' : 'Your agent drafted this page'}
            </div>
            <div className="pg-action-note">
              {live
                ? 'Readers still see the live version until you publish.'
                : 'Readers can’t see it yet.'}
            </div>
          </div>
          <button type="button" className="ink-btn ink-btn-primary" onClick={openChecks}>
            {live ? 'Review and publish' : 'Review and publish page'}
          </button>
        </div>
      ) : null}

      <WorkingPreview
        pageId={row.id}
        path={path}
        device={device}
        outline={showBlocks ? 'blocks' : null}
      />
      <p className="pg-note">
        Read-only preview of the working copy. To change it, ask your agent.
      </p>

      <div className="pg-more">
        <button
          type="button"
          className="pg-more-toggle"
          aria-expanded={checksOpen}
          onClick={() => setChecksOpen((v) => !v)}
        >
          {checksOpen ? '▾' : '▸'} Checks, publishing and history
        </button>
        {checksOpen ? <PageChecks id={row.id} onChanged={onChanged} /> : null}
      </div>

      <div className="pg-delete">
        <button
          type="button"
          className="ink-btn"
          style={{ color: 'var(--ink-red)' }}
          disabled={deleting || isHome}
          title={
            isHome
              ? 'This is your home page. Choose another home page in Site settings first.'
              : undefined
          }
          onClick={() => void handleDelete()}
        >
          {deleting ? 'Deleting…' : 'Delete page'}
        </button>
        {isHome ? (
          <span className="pg-note" style={{ margin: 0 }}>
            The home page can’t be deleted. Choose another one in Site settings first.
          </span>
        ) : null}
        {deleteError ? (
          <span role="alert" style={{ color: 'var(--ink-red)', fontSize: 13 }}>
            {deleteError}
          </span>
        ) : null}
      </div>
    </>
  );
}

/** Header or footer: the home page with that part picked out, and its links. */
function PartPane({ part, site }: { part: Part; site: any }) {
  const [device, setDevice] = useState<Device>('desktop');
  const pending = Boolean(site?.state?.hasUnpublishedChanges);
  const nav = site?.working?.navigation;
  return (
    <>
      <div className="pg-head">
        <div style={{ minWidth: 0 }}>
          <div className="pg-title">{part === 'header' ? 'Header' : 'Footer'}</div>
          <div className="pg-meta">
            <span
              className="pg-status"
              style={{ color: pending ? 'var(--ink-amber)' : 'var(--ink-green-ink)' }}
            >
              <span
                className="pg-dot"
                style={{ background: pending ? 'var(--ink-amber)' : 'var(--ink-green)' }}
              />
              {pending ? 'Changes pending' : 'Live'}
            </span>
            <span>·</span>
            <span>On every page</span>
          </div>
        </div>
        <div className="pg-tools">
          <DeviceSwitch device={device} onDevice={setDevice} />
          <PreviewButton label="Preview" />
        </div>
      </div>

      {pending ? (
        <div className="pg-action">
          <span
            className="pg-dot"
            style={{ background: 'var(--ink-amber)', width: 8, height: 8 }}
          />
          <div className="pg-action-text">
            <div className="pg-action-title">The header and footer have changes</div>
            <div className="pg-action-note">
              They publish with your site’s theme. Readers see the live version until then.
            </div>
          </div>
          <Link to="/site#theme" className="ink-btn ink-btn-primary">
            Review in Site
          </Link>
        </div>
      ) : null}

      <WorkingPreview path="/" device={device} outline={part} />
      <p className="pg-note">
        Read-only preview of the working copy. To change it, ask your agent.
      </p>

      <div className="pg-more">
        <CmsNavigation
          navigation={nav}
          homePage={site?.working?.layout?.homePage}
          showTagline={site?.working?.layout?.showTagline}
        />
      </div>
    </>
  );
}

/** A page's checks, publish controls and approval history. */
function PageChecks({ id, onChanged }: { id: string; onChanged: () => void }) {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_PAGE, {
    variables: { accountId, id },
    skip: !accountId,
    fetchPolicy: 'network-only',
  });
  const [copyEdit, { loading: editing }] = useMutation(RUN_TINYCMS_PAGE_COPY_EDIT);
  const [error, setError] = useState('');
  const page = data?.tinycmsPage;
  if (loading && !page) return <p className="ink-site-row-desc">Loading…</p>;
  if (!page) return null;

  const state = page.state;
  const preflight: Finding[] = state.preflight || [];
  const copy: Finding[] = state.copyEdit?.fresh ? state.copyEdit.findings || [] : [];
  const notRun = !state.copyEdit?.fresh;
  const findings = [
    ...preflight,
    ...(state.copyEdit?.required
      ? copy
      : copy.map((f) => ({ ...f, severity: 'warning' as const }))),
  ];
  const blocking =
    findings.filter((f) => f.severity === 'blocking').length +
    (notRun && state.copyEdit?.required ? 1 : 0);

  const runCopyEdit = async () => {
    setError('');
    try {
      await copyEdit({ variables: { accountId, id } });
      await refetch();
      onChanged();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div id="pg-checks" className="pg-checks">
      <div>
        <p className="ink-site-kicker">Checks</p>
        {notRun ? (
          <p className="ink-site-row-desc" style={{ marginBottom: 8 }}>
            The copy edit hasn’t run on this version
            {state.copyEdit?.required ? '. It’s needed before you can publish' : ''}.
          </p>
        ) : null}
        <FindingsList findings={findings} empty="No findings." />
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            className="ink-btn ink-btn-quiet"
            onClick={() => void runCopyEdit()}
            disabled={editing}
          >
            {editing ? 'Checking…' : 'Run copy edit'}
          </button>
        </div>
        {error ? (
          <p className="text-xs text-error" style={{ marginTop: 6 }}>
            {error}
          </p>
        ) : null}
      </div>

      <div>
        <p className="ink-site-kicker">Publish</p>
        <PublishPanel
          kind="page"
          id={id}
          workingHash={page.workingHash}
          state={state}
          blocking={blocking}
          onDone={() => {
            void refetch();
            onChanged();
          }}
        />
      </div>

      <div>
        <p className="ink-site-kicker">History</p>
        <ApprovalHistory kind="page" subjectId={id} limit={10} />
      </div>
    </div>
  );
}

/**
 * An article's working copy, full screen ("TinyCMS UX optimization review
 * v5.zip", Prototype v3 → article Preview): the site's theme at desktop or
 * phone width, a link to open it in a tab, and ✕ or Escape to close.
 */
export function ArticlePreview({
  articleId,
  title,
  onClose,
}: {
  articleId: string;
  title: string;
  onClose: () => void;
}) {
  const [device, setDevice] = useState<Device>('desktop');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return (
    <div className="pv-overlay" role="dialog" aria-modal="true" aria-label="Preview">
      <div className="pv-bar">
        <span className="pv-bar-title">Preview</span>
        <span className="pv-bar-sub">{title}</span>
        <span className="pv-bar-tools">
          <DeviceSwitch device={device} onDevice={setDevice} />
          <PreviewButton articleId={articleId} label="Open in new tab" />
          <button
            type="button"
            className="pv-close"
            title="Close (Esc)"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </span>
      </div>
      <div className="pv-body">
        <WorkingPreview articleId={articleId} device={device} height="calc(100vh - 160px)" />
        <p className="pg-note">
          Working copy, in your site’s theme. Readers see the published version.
        </p>
      </div>
    </div>
  );
}
